import { useEffect, useRef, useState } from "react";
import type { OperationsAttentionKey, OperationsFacetCountDto, OperationsItemsFacetsDto, OperationsPenKey, OperationsStartRiskKey } from "@/api/endpoints";
import { ATTENTION_CLASS, ATTENTION_LABEL, PEN_LABEL, START_RISK_CLASS, START_RISK_LABEL, workTypeLabel } from "../operationsLabels";
import { pendingWithLabel, type DrawerView } from "../drawerView";
import { MultiSelect, type MultiSelectOption } from "./MultiSelect";

interface Props {
  view: DrawerView;
  facets: OperationsItemsFacetsDto | undefined;
  /** On an owner drill the bucket is one owner: no Owner / Team dropdowns. */
  hideOwner?: boolean;
  hideTeam?: boolean;
  hideDivision?: boolean;
  /** The bucket is one manager's teams (2026-10-27): the Manager dropdown is hidden. */
  hideManager?: boolean;
  onChange: (next: Partial<DrawerView>) => void;
}

/** Wait this long after the last keystroke before the search goes to the URL (and the API). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The filter bar above the drawer table (Rafael, 2026-10-26, scenario 2 of docs/ClaudeDesign/drawer-filters-mock.html):
 * row one keeps the two short, colour-coded groups as chips (Attention, Start date), whose counts the scrum reads
 * at a glance; row two holds the long groups as dropdowns (Pending with, Work type, Team, Owner) with the search.
 * Multi-select everywhere: OR within a group, AND across; counts over the whole bucket with the other groups'
 * selections applied. A chip or option with a count of 0 is hidden unless selected; a group left with a single
 * value and no selection is hidden (it could not narrow anything).
 */
export function DrawerFilterBar({ view, facets, hideOwner = false, hideTeam = false, hideDivision = false, hideManager = false, onChange }: Props) {
  return (
    <>
      <div className="ops-drawer-filters">
        <FacetGroup
          label="Attention"
          values={facets?.attention ?? []}
          selected={view.attention}
          labelOf={(v) => ATTENTION_LABEL[v as OperationsAttentionKey] ?? v}
          dotClass={(v) => `att-${ATTENTION_CLASS[v as OperationsAttentionKey] ?? "unknown"}`}
          onChange={(attention) => onChange({ attention: attention as OperationsAttentionKey[] })}
        />
        <FacetGroup
          label="Start date"
          values={facets?.startRisk ?? []}
          selected={view.startRisk}
          labelOf={(v) => START_RISK_LABEL[v as OperationsStartRiskKey] ?? v}
          dotClass={(v) => `risk-${START_RISK_CLASS[v as OperationsStartRiskKey] ?? "none"}`}
          onChange={(startRisk) => onChange({ startRisk: startRisk as OperationsStartRiskKey[] })}
        />
        <FacetGroup
          label="Who holds the pen"
          values={facets?.holder ?? []}
          selected={view.holder}
          labelOf={(v) => PEN_LABEL[v as OperationsPenKey] ?? v}
          dotClass={(v) => `holder-${v}`}
          onChange={(holder) => onChange({ holder: holder as OperationsPenKey[] })}
        />
      </div>
      <div className="ops-drawer-filters ops-drawer-filters-row2">
        <FacetSelect id="ops-df-pending" label="Pending with" values={facets?.pendingWith ?? []} selected={view.pendingWith} labelOf={pendingWithLabel} onChange={(pendingWith) => onChange({ pendingWith })} />
        <FacetSelect id="ops-df-worktype" label="Work type" values={facets?.workType ?? []} selected={view.workType} labelOf={workTypeLabel} onChange={(workType) => onChange({ workType })} />
        <FacetSelect id="ops-df-engagement" label="Engagement type" values={facets?.engagement ?? []} selected={view.engagement} labelOf={(v) => v} onChange={(engagement) => onChange({ engagement })} />
        {!hideOwner && !hideDivision && <FacetSelect id="ops-df-division" label="Division" values={facets?.division ?? []} selected={view.division} labelOf={(v) => v} onChange={(division) => onChange({ division })} />}
        {!hideOwner && !hideManager && <FacetSelect id="ops-df-manager" label="Manager" values={facets?.manager ?? []} selected={view.manager} labelOf={(v) => v} onChange={(manager) => onChange({ manager })} />}
        {!hideOwner && !hideTeam && <FacetSelect id="ops-df-team" label="Team" values={facets?.team ?? []} selected={view.team} labelOf={(v) => v} onChange={(team) => onChange({ team })} />}
        {!hideOwner && <FacetSelect id="ops-df-owner" label="Owner" values={facets?.owner ?? []} selected={view.owner} labelOf={(v) => v} onChange={(owner) => onChange({ owner })} />}
        <SearchBox value={view.q} onChange={(q) => onChange({ q })} />
      </div>
    </>
  );
}

/**
 * A facet as a dropdown: the slicer bar's MultiSelect with the count at the right of each option. Options at 0
 * are dropped unless selected; a facet with fewer than two options and no selection is hidden.
 */
export function FacetSelect({
  id,
  label,
  values,
  selected,
  labelOf,
  onChange,
}: {
  id: string;
  label: string;
  values: OperationsFacetCountDto[];
  selected: string[];
  labelOf: (value: string) => string;
  onChange: (next: string[]) => void;
}) {
  const options: MultiSelectOption[] = values
    .filter((v) => v.count > 0 || selected.includes(v.value))
    .map((v) => ({ value: v.value, label: labelOf(v.value), count: v.count }));
  if (options.length < 2 && selected.length === 0) return null;
  return <MultiSelect id={id} label={label} options={options} selected={selected} onChange={onChange} />;
}

export function FacetGroup({
  label,
  values,
  selected,
  labelOf,
  dotClass,
  onChange,
}: {
  label: string;
  values: OperationsFacetCountDto[];
  selected: string[];
  labelOf: (value: string) => string;
  dotClass?: (value: string) => string;
  onChange: (next: string[]) => void;
}) {
  // Keep a selected value as a chip even when nothing matches it right now, so it can be turned off; drop the rest at 0.
  const shown = [...values.filter((v) => v.count > 0 || selected.includes(v.value)), ...selected.filter((s) => !values.some((v) => v.value === s)).map((value) => ({ value, count: 0 }))];
  if (shown.length < 2 && selected.length === 0) return null;
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);

  return (
    <div className="ops-facet" role="group" aria-label={label}>
      <span className="ops-facet-label">{label}</span>
      {shown.map((f) => {
        const on = selected.includes(f.value);
        return (
          <button
            key={f.value}
            type="button"
            className="ops-chip"
            aria-pressed={on}
            disabled={f.count === 0 && !on}
            onClick={() => toggle(f.value)}
          >
            {dotClass && <i className={`ops-chip-dot ${dotClass(f.value)}`} aria-hidden="true" />}
            {labelOf(f.value)}
            <span className="n">{f.count}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder = "Worker, client or request ID" }: { value: string; onChange: (q: string) => void; placeholder?: string }) {
  const [draft, setDraft] = useState(value);
  const lastSent = useRef(value);

  // Follow the URL when it changes from outside (Clear filters, another drill).
  useEffect(() => {
    if (value !== lastSent.current) {
      lastSent.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => {
    if (draft === lastSent.current) return;
    const timer = window.setTimeout(() => {
      lastSent.current = draft;
      onChange(draft);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, onChange]);

  return (
    <label className="ops-drawer-search">
      <span className="material-symbols-rounded" aria-hidden="true">search</span>
      <input
        type="search"
        aria-label={`Search ${placeholder.charAt(0).toLowerCase()}${placeholder.slice(1)}`}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
    </label>
  );
}
